import { ResourceClient, type HttpClient, type JobDTO } from "@dl/web-core-js/api";

/**
 * A production pipeline lot: one product's planned production run, with the
 * scores and forecasts the planner used to size and order it.
 *
 * Scores and forecasts come in start/end pairs — the value at the beginning of
 * the planning window and at its end — which is what makes a lot's trajectory
 * legible rather than just its current state.
 */
export interface PipelineLotMetadataDTO {
  group_key: string;
  /** First sale within the grace period. Says nothing about production runs. */
  new_product: boolean;
  has_paint: boolean;
  /** New, and never produced: the first run, shown as JUST LAUNCHED. */
  first_batch: boolean;
}

export interface PipelineLotScoringDataDTO {
  performance_score_start: number;
  performance_score_end: number;
  priority_score_start: number;
  priority_score_end: number;
  weeks_to_oos_start: number;
  weeks_to_oos_end: number;
}

export interface PipelineLotPlanningDataDTO {
  /**
   * The tier the lot was planned in: a run starts at or below `reorder_weeks`
   * of cover and is sized to land at `target_weeks`. Copied at plan time so
   * the row still explains itself after the tiers change.
   */
  reorder_weeks: number;
  target_weeks: number;
  demand_forecast_start: number;
  demand_forecast_end: number;
  inventory_deficit_start: number;
  inventory_deficit_end: number;
  lot_size_start: number;
  lot_size_end: number;
  /**
   * Weekly units per market, counted only where the product is sellable. A
   * product with no US dispatch rule reports 0 here however many USD units it
   * sold historically.
   */
  weekly_units_ca: number;
  weekly_units_us: number;
  /**
   * The warehouse whose cover set weeks_to_oos_end, and its own lead time.
   *
   * Cover is the minimum across the warehouses a product stocks at, so exactly
   * one binds. Naming it lets the grid say why a lot is urgent rather than
   * showing an unattributed subtraction. Null when the product has no demand.
   */
  binding_warehouse_id?: number | null;
  binding_warehouse_code?: string | null;
  /** Paint plus transit to the binding warehouse. Whole weeks, one real route. */
  binding_lead_weeks: number;
  /**
   * The network-wide planning floor the lot was sized against — the safety
   * stock the product carried at plan time. Zero for most products.
   */
  safety_stock_quantity?: number;
  /**
   * The floor, not a warehouse, set `weeks_to_oos_end`: network stock reaches
   * the reserve before any warehouse runs dry. OUT then means "at reserve".
   * `binding_warehouse_id` is null when this is set.
   */
  binding_reserve?: boolean;
}

/**
 * One warehouse's slice of a lot.
 *
 * There is one lot per product because there is one plant, and lot sizing is a
 * production constraint. These rows carry what the network figure cannot: a
 * warehouse can be days from empty while the network is comfortable, and that
 * is a transfer rather than a production run.
 */
export interface PipelineLotWarehouseDTO {
  id: number;
  lot_id: number;
  warehouse_id: number;
  warehouse_code?: string | null;
  demand_weekly: number;
  available_quantity: number;
  weeks_to_oos: number;
  /** Raw shortfall against the window, before any transfer. */
  inventory_deficit: number;
  /** Suggested share of the lot for this warehouse. */
  allocation: number;
  /** Paint plus transit along the lane that reaches this warehouse. */
  lead_weeks: number;
  /** Units the planner recommends moving in from another warehouse. */
  transfer_in: number;
  /** Units it recommends sending out. */
  transfer_out: number;
  /**
   * Shortfall surviving every transfer that could reach here. Non-zero means
   * the rest of the network cannot fill this warehouse, whatever it holds
   * elsewhere, so this is the number that says produce rather than move.
   */
  production_need: number;
}

export interface PipelineLotDTO {
  id: number;
  product_id: number;
  created_at?: string;
  updated_at?: string;
  production_started: boolean;
  metadata?: PipelineLotMetadataDTO;
  scoring_data?: PipelineLotScoringDataDTO;
  planning_data?: PipelineLotPlanningDataDTO;
  /** SIGM's parts for the product (90-<sku> and its box parts), for making the run in SIGM during the shadow period. */
  sigm_part_numbers?: string[];
  /** The lot broken down by the warehouses it feeds, in warehouse-code order. */
  warehouses?: PipelineLotWarehouseDTO[];
}

/**
 * Cover for a product with no demand — mirrors the server's
 * `ProductionPipelineBuilderService.WEEKS_TO_OOS_UNBOUNDED`.
 *
 * It is a sentinel, not a number of weeks. Both columns behind it are
 * `smallint`, so this is the largest value that fits.
 */
export const WEEKS_TO_OOS_UNBOUNDED = 32767;

/** Weeks of cover for display: the sentinel becomes a dash. */
export function weeksToOosLabel(weeks: number): string {
  return weeks >= WEEKS_TO_OOS_UNBOUNDED ? "∞" : String(weeks);
}

export class PipelineLotClient extends ResourceClient<PipelineLotDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/pipeline/lot");
  }

  /** Every lot, in pipeline order. */
  getPipeline(): Promise<PipelineLotDTO[]> {
    return this.http.get<PipelineLotDTO[]>("/production/pipeline/lot/pipeline");
  }

  /**
   * Recomputes the whole pipeline in the background.
   *
   * The synchronous form of this call is what used to hold a request thread —
   * and a modal spinner — for the duration. This returns a job to watch in the
   * job center instead.
   */
  compute(): Promise<JobDTO> {
    return this.http.post<JobDTO>("/production/pipeline/lot/compute", undefined, {
      query: { async: true },
    });
  }

  /** Material schedule workbook for the current pipeline. */
  documentMaterial() {
    return this.http.job("/production/pipeline/lot/document/material");
  }

  /** Historical material purchase schedule workbook. */
  documentMaterialHistorical() {
    return this.http.job("/production/pipeline/lot/document/material/historical");
  }

  /** Units produced over a period — the header's Output badge. */
  productionOutput(dateFrom: string, dateTo: string): Promise<number> {
    return this.http.get<number>("/production/project/output", {
      query: { date_from: dateFrom, date_to: dateTo },
    });
  }
}
